'use client';

import { useEffect, useState, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '../../components/button';
import { LegacyDialog } from '../../components/dialog';
import { Text } from '../../components/text';
import { MetaDefault } from '../../components/typography';
import { Progress } from '../../components/progress';
import { Switch } from '../../components/switch';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  AlertCircleIcon,
  Tick01Icon,
  FileDownIcon,
  File01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';

export type ExportStatus =
  | 'idle'
  | 'preparing'
  | 'generating'
  | 'downloading'
  | 'completed'
  | 'failed';
type FindingsExportDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onStartExport?: (options: { includeRemediation: boolean }) => Promise<void>;
  isExporting: boolean;
  exportStatus: ExportStatus;
  progress: number;
  findingsCount: number;
  error?: string;
  onCancel?: () => void;
};
const STATUS_KEYS: Record<ExportStatus, string> = {
  idle: 'status.idle',
  preparing: 'status.preparing',
  generating: 'status.generating',
  downloading: 'status.downloading',
  completed: 'status.completed',
  failed: 'status.failed',
};
const STATUS_PROGRESS: Record<ExportStatus, number> = {
  idle: 0,
  preparing: 20,
  generating: 60,
  downloading: 90,
  completed: 100,
  failed: 0,
};

export function FindingsExportDialog({
  isOpen,
  onOpenChange,
  onStartExport,
  isExporting,
  exportStatus,
  progress,
  findingsCount,
  error,
  onCancel,
}: FindingsExportDialogProps) {
  const t = useTranslations('designSystem.findings.findingsExportDialog');
  const [includeRemediation, setIncludeRemediation] = useState(false);
  const [isStartingExport, setIsStartingExport] = useState(false);
  const showConfiguration = !isExporting && exportStatus === 'idle';

  useEffect(() => {
    if (exportStatus === 'completed') {
      const timer = setTimeout(() => {
        onOpenChange(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [exportStatus, onOpenChange]);
  useEffect(() => {
    if (!isOpen) {
      setIncludeRemediation(false);
      setIsStartingExport(false);
    }
  }, [isOpen]);
  const handleStartExport = useCallback(async () => {
    if (!onStartExport) {
      return;
    }

    setIsStartingExport(true);

    try {
      await onStartExport({ includeRemediation });
    } finally {
      setIsStartingExport(false);
    }
  }, [includeRemediation, onStartExport]);
  const handleClose = () => {
    // Only allow closing if not exporting or export is complete/failed
    if (
      !isExporting ||
      exportStatus === 'completed' ||
      exportStatus === 'failed'
    ) {
      onOpenChange(false);
    } else if (onCancel) {
      // If a cancel handler is provided, call it (for cancellable exports)
      onCancel();
    }
    // Otherwise, block closing during active export (consistent with LegacyDialog.Root's onOpenChange guard)
  };

  const getProgressColor = () => {
    switch (exportStatus) {
      case 'failed':
        return 'red';
      default:
        return 'terra';
    }
  };

  const displayProgress = progress || STATUS_PROGRESS[exportStatus];

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (
          !isExporting ||
          exportStatus === 'completed' ||
          exportStatus === 'failed'
        ) {
          onOpenChange(details.open);
        }
      }}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content width="400px">
          <VStack gap="6" p="6">
            <HStack gap="3" alignItems="center" w="full">
              <Box
                p="2"
                borderRadius="md"
                bg={exportStatus === 'failed' ? 'bg.error' : 'bg.muted'}
              >
                {exportStatus === 'completed' ? (
                  <Tick01Icon size={20} />
                ) : exportStatus === 'failed' ? (
                  <AlertCircleIcon size={20} />
                ) : (
                  <FileDownIcon size={20} />
                )}
              </Box>
              <VStack gap="0" alignItems="flex-start" flex={1}>
                <LegacyDialog.Title>{t('title')}</LegacyDialog.Title>
                <MetaDefault color="fg.muted">
                  {t('subtitle', { count: findingsCount })}
                </MetaDefault>
              </VStack>
            </HStack>

            {showConfiguration ? (
              <VStack gap="4" w="full">
                <Box p="3" bg="bg.subtle" borderRadius="md" w="full">
                  <Text fontSize="sm" color="fg.muted">
                    {t('configDescription')}
                  </Text>
                </Box>
                <HStack
                  justifyContent="space-between"
                  w="full"
                  p="3"
                  bg="bg.subtle"
                  borderRadius="md"
                >
                  <VStack gap="0.5" alignItems="flex-start">
                    <Text fontSize="sm" fontWeight="medium">
                      {t('includeRemediation')}
                    </Text>
                    <Text fontSize="xs" color="fg.muted">
                      {t('remediationHint')}
                    </Text>
                  </VStack>
                  <Switch
                    size="sm"
                    checked={includeRemediation}
                    onCheckedChange={(details) =>
                      setIncludeRemediation(details.checked === true)
                    }
                  />
                </HStack>
              </VStack>
            ) : (
              <VStack gap="3" w="full">
                <VStack gap="2" w="full">
                  <HStack gap="2" alignItems="center" colorPalette="terra">
                    {isExporting &&
                      exportStatus !== 'completed' &&
                      exportStatus !== 'failed' && (
                        <Box
                          w="2"
                          h="2"
                          borderRadius="full"
                          bg="colorPalette.default"
                          animation="pulse 1.5s infinite"
                        />
                      )}
                    <Text fontSize="sm" fontWeight="medium">
                      {t(STATUS_KEYS[exportStatus])}
                    </Text>
                  </HStack>
                  <Progress
                    value={displayProgress}
                    max={100}
                    size="sm"
                    colorPalette={getProgressColor()}
                  />
                </VStack>

                <VStack gap="1" w="full" mt="2">
                  <StageIndicator
                    label={t('stages.preparingData')}
                    isActive={exportStatus === 'preparing'}
                    isComplete={[
                      'generating',
                      'downloading',
                      'completed',
                    ].includes(exportStatus)}
                    isFailed={exportStatus === 'failed'}
                  />
                  <StageIndicator
                    label={t('stages.generatingPdf')}
                    isActive={exportStatus === 'generating'}
                    isComplete={['downloading', 'completed'].includes(
                      exportStatus,
                    )}
                    isFailed={exportStatus === 'failed'}
                  />
                  <StageIndicator
                    label={t('stages.downloading')}
                    isActive={exportStatus === 'downloading'}
                    isComplete={exportStatus === 'completed'}
                    isFailed={exportStatus === 'failed'}
                  />
                </VStack>

                {exportStatus === 'failed' && error && (
                  <HStack
                    gap="2"
                    p="3"
                    bg="bg.error"
                    borderRadius="md"
                    w="full"
                    mt="2"
                  >
                    <AlertCircleIcon size={16} />
                    <Text fontSize="sm" color="fg.error">
                      {error}
                    </Text>
                  </HStack>
                )}

                {exportStatus === 'completed' && (
                  <HStack
                    gap="2"
                    p="3"
                    bg="bg.muted"
                    borderRadius="md"
                    w="full"
                    mt="2"
                  >
                    <Tick01Icon size={16} />
                    <Text fontSize="sm">{t('downloadSuccess')}</Text>
                  </HStack>
                )}
              </VStack>
            )}

            <HStack gap="3" w="full" justifyContent="flex-end">
              {showConfiguration ? (
                <>
                  <Button variant="outline" onClick={handleClose}>
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={handleStartExport}
                    disabled={!onStartExport}
                    loading={isStartingExport}
                  >
                    <File01Icon size={16} />
                    {t('generatePdf')}
                  </Button>
                </>
              ) : exportStatus === 'completed' || exportStatus === 'failed' ? (
                <Button onClick={handleClose}>{t('close')}</Button>
              ) : (
                <Button variant="outline" onClick={handleClose}>
                  {t('cancel')}
                </Button>
              )}
            </HStack>
          </VStack>

          <LegacyDialog.CloseTrigger
            asChild
            position="absolute"
            top="2"
            right="2"
          >
            <Button
              variant="ghost"
              size="sm"
              aria-label="Close Dialog"
              onClick={handleClose}
            >
              <Cancel01Icon size={16} />
            </Button>
          </LegacyDialog.CloseTrigger>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}

function StageIndicator({
  label,
  isActive,
  isComplete,
  isFailed,
}: {
  label: string;
  isActive: boolean;
  isComplete: boolean;
  isFailed: boolean;
}) {
  const getDotColor = () => {
    if (isFailed && isActive) return 'fg.error';
    if (isComplete) return 'green.9';
    if (isActive) return 'colorPalette.default';

    return 'fg.subtle';
  };

  const getTextColor = () => {
    if (isFailed && isActive) return 'fg.error';
    if (isComplete || isActive) return 'fg.default';

    return 'fg.muted';
  };

  return (
    <HStack gap="2" w="full" colorPalette="terra">
      <Box
        w="2"
        h="2"
        borderRadius="full"
        bg={getDotColor()}
        flexShrink={0}
        animation={
          isActive && !isComplete && !isFailed
            ? 'pulse 1.5s infinite'
            : undefined
        }
      />
      <Text fontSize="xs" color={getTextColor()}>
        {label}
      </Text>
      {isComplete && <Tick01Icon size={12} color="var(--colors-green-9)" />}
    </HStack>
  );
}
